import React, { FC, useEffect, useState } from 'react';
import { MapContainer, MapContainerProps } from 'react-leaflet';
import FieldsByMapType from '../fieldsByMapType/FieldsByMapType';
import { MapMenuType, MapModeEnum } from '../../slice/types';
import { useMapSlice } from '../../slice';
import { useDispatch, useSelector } from 'react-redux';

import * as L from 'leaflet';

import { useTranslation } from 'react-i18next';
import * as S from './MapCard.styles';

import {
  selectMapMode,
  selectShowCurrentUserLocation,
} from '../../slice/selectors';

import OverlayMenus from '../OverlayMenus/OverlayMenus';
import ZoomControl from '../ZoomControl/ZoomControl';
import BaseTileLayers from '../tileLayers/BaseTileLayers/BaseTileLayers';
import WmsTileLayers from '../tileLayers/WmsTileLayers/WmsTileLayers';
import MapLoadingToast from '../MapLoadingToast/MapLoadingToast';
import MapCardHeaderButtons from '../MapCardHeaderButtons/MapCardHeaderButtons';
import TransportMarkers from '../TransportMarkers/TransportMarkers';
import CurrentUserLocation from '../CurrentUserLocation/CurrentUserLocation';
import { DEFAULT_MAP_BOUNDS } from '../../slice/map-constants';

interface IProps {
  mode: MapModeEnum;
  title?: string;
  mapContainerProps?: MapContainerProps;
  readOnly?: boolean;
  extraHeaderButtons?: JSX.Element[];

  showLayerMenu?: boolean;
  showCulturesMenu?: boolean;
  showSelectFieldsByCulture?: boolean;
}

const MapCard: FC<IProps> = ({
  mode,
  title,
  mapContainerProps,
  readOnly,
  extraHeaderButtons,

  showCulturesMenu = true,
  showLayerMenu = true,
  showSelectFieldsByCulture = false,
}) => {
  const { actions } = useMapSlice();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const modeInState = useSelector(selectMapMode);
  const showCurrentUserLocation = useSelector(selectShowCurrentUserLocation);

  const [activeMapMenu, setActiveMapMenu] = useState<MapMenuType>(null);

  useEffect(() => {
    if (mode !== modeInState) {
      dispatch(actions.setMapMode(mode));
    }
    dispatch(actions.toggleActivePolygonId(null));
  }, [mode, modeInState, dispatch, actions]);

  useEffect(() => {
    return () => {
      dispatch(actions.setIsMapReady(false));
    };
  }, [actions, dispatch]);

  const setMapReady = () => {
    dispatch(actions.setIsMapReady(true));
  };

  return (
    <S.Card
      bordered={false}
      title={title || t('Map.Title', 'Map')}
      extra={
        <MapCardHeaderButtons
          extraHeaderButtons={extraHeaderButtons}
          showCulturesMenu={showCulturesMenu}
          showLayerMenu={showLayerMenu}
          showSelectFieldsByCulture={showSelectFieldsByCulture}
          activeMapMenu={activeMapMenu}
          setActiveMapMenu={setActiveMapMenu}
        />
      }
      bodyStyle={{
        padding: 0,
        // height 100% minus height of card header
        height: 'calc(100% - 58px)',
        display: 'flex',
      }}
    >
      <S.MapWrapper>
        <MapContainer
          renderer={L.canvas()}
          maxZoom={19}
          whenReady={() => setMapReady()}
          bounds={DEFAULT_MAP_BOUNDS}
          {...mapContainerProps}
        >
          {showCurrentUserLocation && <CurrentUserLocation />}
          <BaseTileLayers />

          <FieldsByMapType readOnly={readOnly} mode={modeInState} />
          <TransportMarkers />
          <WmsTileLayers />
          <ZoomControl />
        </MapContainer>
        <OverlayMenus activeMapMenu={activeMapMenu} />
        <MapLoadingToast />
      </S.MapWrapper>
    </S.Card>
  );
};

export default MapCard;
